import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Link } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import { convertObjToArr } from '../../../../../utils';
import ActionsComments from '../../../../../store/ducks/comments';
import ConfirmationDialog from '../../../../../components/ConfirmationDialog';
import Comment from '../../../../../components/Comment';
import InputComment from '../InputComment';
import useStyles from './styles';

const CommentGroup = props => {
  const {
    id,
    userId: authorId,
    userName: author,
    description: msg,
    createdAt: date,
    status,
    allowReply,
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { userId } = useSelector(state => state.userProfile);
  const { list } = useSelector(state => state.course);
  const { course: courseId, lesson: lessonId } = useSelector(
    state => state.course.current,
  );
  const [dialogConfirm, setDialogConfirm] = useState(false);
  const [responsesUpdating, setResponsesUpdating] = useState(true);
  const [commentEditing, setCommentEditing] = useState(null);
  const [, setCommentRemoving] = useState(false);
  const [, setReplyStoring] = useState(false);
  const [responses, setResponses] = useState([]);
  const [stateResponses, setStateResponses] = useState({
    show: false,
    length: responses.length,
    showReply: false,
  });

  useEffect(() => {
    if (list[courseId] && list[courseId].courseDetailed) {
      const lessonInfos = list[courseId].lessons[lessonId];
      if (lessonInfos?.commentsLoaded) {
        const lessonComments = lessonInfos.comments;
        const commentResponses = convertObjToArr(lessonComments).filter(
          item => item.parentId === id,
        );
        setResponses(commentResponses);
      }
    }
  }, [courseId, id, lessonId, list]);

  useEffect(() => {
    setResponsesUpdating(false);
    setDialogConfirm(false);
    setCommentRemoving(oldCommentRemoving => {
      if (oldCommentRemoving) {
        enqueueSnackbar('Comentário removido com sucesso!', {
          variant: 'success',
        });
        return false;
      }
      return oldCommentRemoving;
    });
    setCommentEditing(oldCommentEditing => {
      if (oldCommentEditing) {
        enqueueSnackbar('Comentário editado com sucesso!', {
          variant: 'success',
        });
        return false;
      }
      return oldCommentEditing;
    });
  }, [responses, msg, enqueueSnackbar]);

  useEffect(() => {
    setStateResponses(oldStateResponses => ({
      ...oldStateResponses,
      length: responses.length,
      showReply: false,
    }));
    setReplyStoring(oldReplyStoring => {
      if (oldReplyStoring) {
        enqueueSnackbar('Resposta adicionada com sucesso!', {
          variant: 'success',
        });
        return false;
      }
      return oldReplyStoring;
    });
  }, [enqueueSnackbar, responses]);

  const handleToggleShowResponses = useCallback(() => {
    setStateResponses(oldStateResponses => ({
      ...oldStateResponses,
      show: !oldStateResponses.show,
    }));
  }, []);

  const handleShowReply = useCallback(
    (forceHideResponses = false, hideReply = false) => {
      setStateResponses(oldStateResponses => ({
        ...oldStateResponses,
        show: forceHideResponses ? false : oldStateResponses.show,
        showReply: !hideReply,
      }));
    },
    [],
  );

  const handleEditComment = useCallback(
    (commentId = null) => ({
      openEdit: () => {
        setCommentEditing(commentId);
      },
      cancelEdit: () => {
        setCommentEditing(null);
      },
    }),
    [],
  );

  const handleRemoveComment = useCallback(
    commentId => {
      const hasResponses = commentId === id && stateResponses.length;
      const dialogConfig = hasResponses
        ? {
            type: 'error',
            icon: 'error',
            title: 'Este comentário não pode ser removido',
            description:
              'Comentários que possuem respostas não podem ser removidos.',
            confirmText: 'Entendi',
            isOpen: true,
            loading: false,
            confirmCb: () => {
              setDialogConfirm(false);
            },
          }
        : {
            type: 'warning',
            icon: 'warning',
            title: 'Tem certeza que deseja remover o comentário?',
            description: '',
            confirmText: 'Remover',
            cancelText: 'Cancelar',
            isOpen: true,
            loading: false,
            confirmCb: () => {
              setDialogConfirm(false);
              dispatch(
                ActionsComments.requestRemoveComment(
                  courseId,
                  lessonId,
                  commentId,
                ),
              );
              setDialogConfirm({ ...dialogConfig, loading: true });
              setCommentRemoving(true);
            },
            cancelCb: () => {
              setDialogConfirm(false);
            },
          };
      setDialogConfirm(dialogConfig);
    },
    [courseId, dispatch, id, lessonId, stateResponses.length],
  );

  const commentActions = useCallback(
    (commentId, commentOwner) =>
      commentOwner === userId && [
        {
          name: 'Editar',
          cb: () => handleEditComment(commentId).openEdit(),
        },
        {
          name: 'Remover',
          cb: () => handleRemoveComment(commentId),
        },
      ],
    [handleEditComment, handleRemoveComment, userId],
  );

  const setInputComment = useCallback(
    (commentId, commentText) =>
      commentEditing === commentId && {
        ComponentInput: InputComment,
        componentProps: {
          onCancel: () => handleEditComment().cancelEdit(),
          editComment: {
            id: commentId,
            text: commentText,
          },
        },
      },
    [commentEditing, handleEditComment],
  );

  const handleStoreReply = useCallback(() => {
    setReplyStoring(true);
  }, []);

  return (
    <Box className={classes.container}>
      <Comment
        title={author}
        msg={msg}
        date={date}
        status={status}
        responses={stateResponses}
        toggleResponses={handleToggleShowResponses}
        toggleReply={allowReply ? () => handleShowReply(true) : false}
        actions={commentActions(id, authorId)}
        editComment={setInputComment(id, msg)}
      />
      {stateResponses.show || stateResponses.showReply ? (
        <Box className="comments-group-responses">
          <>
            {stateResponses.show &&
              responses.map(response => (
                <Comment
                  key={response.id}
                  title={response.userName}
                  msg={response.description}
                  date={response.createdAt}
                  status={response.status}
                  actions={commentActions(response.id, response.userId)}
                  editComment={setInputComment(
                    response.id,
                    response.description,
                  )}
                  isReply
                />
              ))}
            {stateResponses.showReply ? (
              <InputComment
                replyComment={{ parentId: id }}
                onCancel={() => handleShowReply(false, true)}
                onStore={handleStoreReply}
                reset={() => !responsesUpdating}
              />
            ) : (
              <>
                {allowReply ? (
                  <Link
                    className="comments-group-btn-add-reply"
                    underline="always"
                    component="button"
                    onClick={() => handleShowReply(false, false)}
                  >
                    Adicionar resposta
                  </Link>
                ) : null}
              </>
            )}
          </>
        </Box>
      ) : null}
      {dialogConfirm ? <ConfirmationDialog {...dialogConfirm} /> : null}
    </Box>
  );
};

export default CommentGroup;
